import { Fragment, useCallback, useMemo, useRef, useState } from 'react';

import {
  getErrorMessage,
  invalidateAllRulesQuery,
  useRulesJsonUpload,
  useRulesJsonValidate,
  type Rule,
} from '@frontend/api';
import {
  Button,
  Empty,
  EmptyDescription,
  EmptyDrawerIcon,
  EmptyHeadline,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Modal,
  ModalBody,
  ModalButtonGroup,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  OutlineSquarePlusIcon,
  TableContainer,
  TableV2,
  Tbody,
  Td,
  Text,
  Textarea,
  Tr,
  VStack,
  Checkbox,
  useToast,
  Flex,
  FormErrorMessage,
  ShareExternalIcon,
} from '@frontend/components';
import { useCopyToClipboard } from '@frontend/utils';

import PartUploader from 'components/PartUploader/PartUploader';

import { pluralize } from 'utils/stringUtils';

import { useRulesImportState } from '../hooks/useRulesImportState';
import type {
  ImportCandidate,
  ImportResultCategory,
} from '../types/rulesImport';
import {
  getResolutionIcon,
  getIsDestructive,
  getResolutionKey,
} from '../utils/resolutions';
import {
  formatValidationErrorsForCopy,
  groupValidationErrorsByRule,
} from '../utils/validationErrors';
import { RulesDetailModal } from './RulesDetailModal';

type RulesImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const CATEGORY_TITLES: Record<'created' | 'updated' | 'errors', string> = {
  created: 'Created',
  updated: 'Updated',
  errors: 'Invalid',
};

const parseRulesJson = (jsonString: string): Partial<Rule>[] => {
  const parsed = JSON.parse(jsonString);
  return Array.isArray(parsed) ? parsed : [parsed];
};

const isValidRulesJson = (jsonString: string): boolean => {
  if (!jsonString.trim()) {
    return false;
  }

  try {
    const rules = parseRulesJson(jsonString);

    // Check if all items in the array are objects that could be partial rules
    return rules.every((rule) => {
      if (typeof rule !== 'object' || rule === null) {
        return false;
      }

      // Check for at least one property that would indicate this is a rule-like object
      // We're being lenient here since it's a partial rule validation
      const hasRuleProperty =
        'name' in rule ||
        'description' in rule ||
        'signals' in rule ||
        'resolutions' in rule ||
        'logicalOperator' in rule ||
        'uuid' in rule;

      return hasRuleProperty;
    });
  } catch {
    return false;
  }
};

export const RulesImportModal = ({
  isOpen,
  onClose,
}: RulesImportModalProps) => {
  const toast = useToast();
  const [jsonInput, setJsonInput] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isFixOpen, setIsFixOpen] = useState<{ open: boolean; id?: string }>({
    open: false,
  });
  const jsonTextareaRef = useRef<HTMLTextAreaElement>(null);
  const {
    candidates,
    created,
    updated,
    errors,
    selectedIds,
    replaceCandidates,
    mergeCandidates,
    setSelected,
    selectMany,
  } = useRulesImportState();

  const { mutate: validateRules, isLoading: isValidating } =
    useRulesJsonValidate({
      queryOptions: {
        onSuccess: (res) => {
          const items: ImportCandidate[] = [
            ...res.created.map(({ rule, requiresRecalc }) => ({
              id: rule.uuid,
              rule,
              requiresRecalc,
              category: 'created' as const,
            })),
            ...res.updated.map(({ rule, requiresRecalc }) => ({
              id: rule.uuid,
              rule,
              requiresRecalc,
              category: 'updated' as const,
            })),
            ...res.errors.map(({ rule, errors }) => ({
              id: rule.uuid,
              rule,
              validationErrors: errors,
              category: 'errors' as const,
            })),
          ];
          replaceCandidates(items);
          selectMany(
            items.filter((c) => c.category !== 'errors').map((c) => c.id),
          );
          toast({ status: 'success', title: 'Rules validated successfully' });
        },
        onError: () => {
          toast({ status: 'error', title: 'Failed to validate rules' });
        },
      },
    });

  const copyToClipboard = useCopyToClipboard({
    onSuccess: () => {
      toast({ status: 'success', title: 'Copied errors to clipboard' });
    },
    onFailure: () => {
      toast({ status: 'error', title: 'Failed to copy errors' });
    },
  });

  const isValidJson = useMemo(() => {
    return isValidRulesJson(jsonInput);
  }, [jsonInput]);

  const canSubmitRead = useMemo(() => {
    return isValidJson || selectedFiles.length > 0;
  }, [isValidJson, selectedFiles.length]);

  const handleFileUpload = useCallback(
    (file: File | File[]) => {
      const files = Array.isArray(file) ? file : [file];
      const picked = files.find(
        (f) =>
          f &&
          (f.name?.toLowerCase().endsWith('.json') ||
            f.type === 'application/json'),
      );
      if (!picked) {
        toast({ status: 'error', title: 'Please upload a .json file' });
        return;
      }
      setSelectedFiles([picked]);
    },
    [toast],
  );

  const handleReadRules = useCallback(() => {
    // Priority: use JSON textarea if present; otherwise send files via FormData
    if (isValidJson) {
      validateRules({ rulesData: parseRulesJson(jsonInput) });
    } else if (selectedFiles.length > 0) {
      const form = new FormData();
      // Append files only; do not parse on client
      const selectedFile = selectedFiles[0];
      form.append('rules_file', selectedFile, selectedFile.name);
      validateRules(form);
    }
  }, [isValidJson, jsonInput, selectedFiles, validateRules]);

  const hasAny = candidates.length > 0;

  const errorsForCopy = useMemo(() => {
    if (errors.length === 0) return '';
    const grouped = groupValidationErrorsByRule(
      errors.map((c) => ({
        ruleName: c.rule.name,
        errors: c.validationErrors ?? [],
      })),
    );
    const parts: string[] = [];
    Object.entries(grouped).forEach(([name, errors]) => {
      parts.push(formatValidationErrorsForCopy(errors, name));
    });
    return parts.join('\n\n');
  }, [errors]);

  const handleCopyErrors = useCallback(() => {
    if (!errorsForCopy) return;
    copyToClipboard(errorsForCopy);
  }, [errorsForCopy, copyToClipboard]);

  const [editingRule, setEditingRule] = useState<Rule | null>(null);

  const openFixFor = useCallback(
    (id: string) => {
      const candidate = candidates.find((c) => c.id === id);
      if (candidate) {
        setEditingRule(candidate.rule);
        setIsFixOpen({ open: true, id });
      }
    },
    [candidates],
  );

  const closeFix = useCallback(() => {
    setIsFixOpen({ open: false });
    setEditingRule(null);
  }, []);

  const handleClearState = useCallback(() => {
    setIsFixOpen({ open: false });
    setEditingRule(null);
    setJsonInput('');
    setSelectedFiles([]);
    replaceCandidates([]);
  }, [replaceCandidates]);

  const handleOverrideSubmit = useCallback(
    (rule: Rule, category?: ImportResultCategory) => {
      const id = isFixOpen.id;
      if (!id) return;

      const existing = candidates.find((c) => c.id === id);
      if (existing) {
        mergeCandidates([
          {
            ...existing,
            rule,
            category: category ?? 'created',
            validationErrors: undefined,
          },
        ]);
        setSelected(id, true);
      } else {
        mergeCandidates([
          {
            id,
            rule,
            category: category ?? 'created',
            validationErrors: undefined,
          },
        ]);
        setSelected(id, true);
      }
    },
    [mergeCandidates, isFixOpen.id, candidates, setSelected],
  );

  const { mutate: uploadRules, isLoading: isUploading } = useRulesJsonUpload({
    queryOptions: {
      onSuccess: (resp) => {
        onClose();
        handleClearState();
        invalidateAllRulesQuery();
        toast({
          status: 'success',
          title: 'Rules uploaded successfully',
          description: `${pluralize('rule', resp.created)} created, ${pluralize(
            'rule',
            resp.updated,
          )} updated`,
        });
      },
      onError: (error) => {
        toast({
          status: 'error',
          title: 'Failed to upload rules',
          description: getErrorMessage(error),
        });
      },
    },
  });

  const handleUploadRules = useCallback(() => {
    const rulesData = candidates
      .filter((c) => c.category !== 'errors' && selectedIds.has(c.id))
      .map((c) => c.rule);
    uploadRules({ rulesData });
  }, [candidates, uploadRules, selectedIds]);

  const renderRow = (c: ImportCandidate) => {
    const hasErrors =
      (c.validationErrors?.length ?? 0) > 0 || c.category === 'errors';
    return (
      <Tr key={c.id}>
        <Td width={0} pr={0}>
          <Checkbox
            isChecked={!hasErrors && selectedIds.has(c.id)}
            isDisabled={hasErrors}
            onChange={(e) => setSelected(c.id, e.currentTarget.checked)}
            marginBottom="auto"
            marginTop={0.25}
          />
        </Td>
        <Td colSpan={hasErrors ? 2 : 1}>
          <HStack justify="space-between" align="center">
            <VStack align="start" spacing={0}>
              <HStack spacing={1} align="center">
                <Heading size="md" m={0}>
                  {c.rule.name || c.id}
                </Heading>
              </HStack>
              <Text size="sm" color="feature.secondary" m={0} noOfLines={2}>
                {c.rule.description ?? ''}
              </Text>
              {hasErrors && (
                <VStack align="start" spacing={0.5}>
                  {(c.validationErrors ?? []).map((error) => (
                    <Text
                      key={error.message}
                      size="sm"
                      color="alert.error"
                      m={0}
                      pl={0}
                    >
                      •{' '}
                      {`${error.path}: ${error.message}${
                        error.offendingValue
                          ? ` (${JSON.stringify(error.offendingValue)})`
                          : ''
                      }`}
                    </Text>
                  ))}
                </VStack>
              )}
            </VStack>
          </HStack>
        </Td>
        {hasErrors ? (
          <Td>
            <Button
              alignSelf="flex-end"
              size="md"
              variant="primary"
              onClick={() => openFixFor(c.id)}
            >
              Fix rule
            </Button>
          </Td>
        ) : (
          <Td>
            <Text size="xs" color="feature.secondary" m={0} whiteSpace="nowrap">
              {pluralize('signal', c.rule.signals?.length ?? 0)}
            </Text>
          </Td>
        )}
        {hasErrors ? (
          <></>
        ) : (
          <Td>
            <HStack gap={1}>
              {(c.rule.resolutions ?? []).map((resolution) => {
                const Icon = getResolutionIcon(resolution.type);
                const isDestructive = getIsDestructive(resolution.type);
                return (
                  <Icon
                    key={getResolutionKey(resolution)}
                    boxSize={2}
                    color={
                      isDestructive
                        ? 'interaction.destructive'
                        : 'feature.secondary'
                    }
                  />
                );
              })}
            </HStack>
          </Td>
        )}
      </Tr>
    );
  };

  const groupedSections = [
    { key: 'errors' as const, data: errors },
    { key: 'created' as const, data: created },
    { key: 'updated' as const, data: updated },
  ];

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        initialFocusRef={jsonTextareaRef}
      >
        <ModalContent
          overflow="hidden"
          maxHeight="calc(100% - 120px)"
          height="max-content"
        >
          <ModalHeader>
            <HStack spacing={2}>
              <Heading size="lg" m={0}>
                Import Rules
              </Heading>
              <Button
                as="a"
                href="https://chatgpt.com/g/g-68c9a28cc9f4819191c2cd533e5d615d-requirements-review-rulesmith"
                variant="link"
                target="_blank"
                size="sm"
                icon={<ShareExternalIcon />}
              >
                Build rules with ChatGPT
              </Button>
              <Button
                as="a"
                href="https://gemini.google.com/gem/1a0618023150"
                variant="link"
                target="_blank"
                size="sm"
                icon={<ShareExternalIcon />}
              >
                Build rules with Gemini
              </Button>
            </HStack>
            <ModalCloseButton />
          </ModalHeader>
          <ModalBody
            p={0}
            as={VStack}
            spacing={0}
            align="stretch"
            overflowY="auto"
          >
            <VStack
              spacing={2}
              bg="page.plateLight"
              p={2}
              alignItems="stretch"
              borderBottom="1px solid"
              borderColor="separator.border"
            >
              <HStack spacing={2} align="start">
                <FormControl
                  flex={1}
                  as={VStack}
                  spacing={0}
                  align="stretch"
                  isInvalid={!isValidJson && jsonInput.trim().length > 0}
                >
                  <FormLabel>Add JSON</FormLabel>
                  <Textarea
                    ref={jsonTextareaRef}
                    placeholder="Paste JSON"
                    resize="none"
                    flex={1}
                    height="auto"
                    value={jsonInput}
                    onChange={(e) => setJsonInput(e.target.value)}
                  />
                  <FormErrorMessage>Invalid JSON format</FormErrorMessage>
                </FormControl>
                <FormControl flex={1} as={VStack} spacing={0} align="stretch">
                  <FormLabel>Add File</FormLabel>
                  {selectedFiles.length === 0 ? (
                    <>
                      <PartUploader
                        type="dropzone"
                        inline
                        multiple={false}
                        onUpload={handleFileUpload}
                        supportingFileUpload
                        defaultUnits=""
                        groupId=""
                        category={undefined as unknown as string}
                        p={0.75}
                        minH={10}
                        border="1px dashed"
                        borderColor="separator.border"
                        borderRadius="sm"
                        m={0}
                        useOverlay={{ zIndex: 1 }}
                      >
                        <Flex
                          align="center"
                          gap={1}
                          justify="flex-start"
                          w="100%"
                        >
                          <OutlineSquarePlusIcon
                            boxSize={3}
                            color="feature.placeholder"
                          />
                          <Flex direction="column">
                            <Text
                              size="sm"
                              fontWeight="medium"
                              color="feature.primary"
                              m={0}
                            >
                              Drag and drop a .json file here or{' '}
                              <PartUploader
                                type="button"
                                inline
                                multiple={false}
                                size="sm"
                                variant="link"
                                supportingFileUpload
                                defaultUnits=""
                                groupId=""
                                useOverlay={false}
                                category={undefined as unknown as string}
                                onUpload={handleFileUpload}
                              >
                                choose file
                              </PartUploader>{' '}
                              to upload
                            </Text>
                            <Text size="sm" color="feature.secondary" m={0}>
                              Only one file will be used
                            </Text>
                          </Flex>
                        </Flex>
                      </PartUploader>
                    </>
                  ) : (
                    <HStack
                      align="center"
                      justify="space-between"
                      border="1px solid"
                      borderColor="separator.border"
                      borderRadius="sm"
                      p={0.75}
                      mt={0.75}
                    >
                      <Text size="sm" m={0}>
                        {selectedFiles[0].name}
                      </Text>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setSelectedFiles([])}
                      >
                        Remove
                      </Button>
                    </HStack>
                  )}
                </FormControl>
              </HStack>

              <Button
                variant="secondary"
                isDisabled={isValidating || !canSubmitRead}
                onClick={handleReadRules}
                isLoading={isValidating}
              >
                Read Rules
              </Button>
            </VStack>

            {hasAny ? (
              <TableContainer
                borderRadius="none"
                border="none"
                variant="simple"
                overflowY="auto"
              >
                <TableV2>
                  <Tbody>
                    {groupedSections
                      .filter((s) => s.data.length > 0)
                      .map((section) => (
                        <Fragment key={section.key}>
                          <Tr position="sticky" top={0} zIndex={1}>
                            <Td
                              colSpan={4}
                              bg="page.plateLight"
                              px={1.5}
                              py={0.5}
                            >
                              <Heading size="md" m={0}>
                                {CATEGORY_TITLES[section.key]}
                              </Heading>
                            </Td>
                          </Tr>
                          {section.data.map((c) => renderRow(c))}
                        </Fragment>
                      ))}
                  </Tbody>
                </TableV2>
              </TableContainer>
            ) : (
              <Empty py={8} flexGrow={1} gap={0.5}>
                <EmptyDrawerIcon />
                <EmptyHeadline>No rules added</EmptyHeadline>
                <EmptyDescription>
                  Add rules by pasting JSON in the text area above or by
                  uploading rule files.
                </EmptyDescription>
              </Empty>
            )}
          </ModalBody>
          <ModalFooter as={VStack} spacing={1.5} alignItems="stretch">
            {/* Errors summary and copy */}
            {errors.length > 0 && (
              <HStack>
                <HStack
                  flex={1}
                  spacing={0}
                  justifyContent="flex-start"
                  align="center"
                >
                  <Text size="md" m={0}>
                    {pluralize(
                      'invalid rule',
                      new Set(errors.map((e) => e.rule.name)).size,
                    )}
                    /{' '}
                    {pluralize(
                      'error',
                      errors.reduce(
                        (acc, c) => acc + (c.validationErrors?.length ?? 0),
                        0,
                      ),
                      'total errors',
                    )}
                  </Text>
                </HStack>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCopyErrors}
                >
                  Copy to clipboard
                </Button>
              </HStack>
            )}
            <HStack align="center" gap={1} justify="space-between">
              <Text m={0} size="md" color="feature.secondary">
                {pluralize('rule', selectedIds.size)} staged
              </Text>
              <ModalButtonGroup flex={1}>
                <Button variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  isDisabled={selectedIds.size === 0}
                  isLoading={isUploading}
                  onClick={handleUploadRules}
                >
                  Import {pluralize('rule', selectedIds.size)}
                </Button>
              </ModalButtonGroup>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <RulesDetailModal
        isOpen={isFixOpen.open}
        onClose={closeFix}
        initialRule={editingRule ?? undefined}
        isReadOnly={false}
        showConfigureRulesButton={false}
        mode="validate"
        onSuccess={handleOverrideSubmit}
      />
    </>
  );
};
